<template>
  <div :class="{ 'sticky-empty-state': !hasResults }">
    <EmptyState
      v-if="!hasResults && !loading"
      class="pa-14"
      :icon="'mdi-bee'"
      :iconSize="48"
      :title="$t('explore.empty_state_title')"
      :message="subtitleEmptyState"
    />
    <ExploreSkeletons
      v-if="loading && !isTableView"
      :cols="smallView ? 4 : 3"
    />
    <template v-else>
      <ExploreContentTable
        v-if="isTableView && hasResults"
        class="ml-2"
        :isLoading="loading"
        :tableOptions="options"
        :results="results"
        :totalItems="totalItems"
        :searchValue="searchValue"
        :selectedHeaders="selectedHeaders"
        @changePage="handleChangePage"
        @changeItems="handleChangeItemsPerPage"
        @sort="$emit('sort', $event)"
        @openFinding="handleOpenFindingDrawer"
      />
      <div v-else>
        <v-row class="ma-0">
          <v-col
            v-for="(item, index) in results"
            cols
            sm="6"
            md="4"
            class="mb-3"
            :key="item.response.id"
            :lg="smallView ? 4 : 3"
          >
            <EntityCard
              :tourId="`explore-entityCard-${index}`"
              :data="item.response"
              :highlights="item.highlights"
              :matchingFields="item.matchingFields"
              :searchValue="searchValue"
              :sort="sort"
              @openFinding="handleOpenFindingDrawer"
              @openSummarizeDialog="handleOpenSummarizeDialog(item.response)"
            />
          </v-col>
        </v-row>
      </div>
    </template>
    <FindingDrawer
      v-if="isFindingDrawerOpen"
      hide-overlay
      :visible="isFindingDrawerOpen"
      :findingId="selectedFindingId"
      @close="handleFindingDrawerClose"
      @deleted="handleFindingDrawerClose"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import mixin from '@/mixins/mixin.vue'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import api from '@/api'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import EntityCard from '@/containers/Explore/EntityCard.vue'
import ExploreSkeletons from './ExploreSkeletons.vue'
import ExploreContentTable from './ExploreContentTable.vue'
import EmptyState from '@/components/EmptyState.vue'

import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'

const VIEW_OPTIONS = {
  cards: 'cards',
  table: 'table',
}

export default {
  name: 'ExploreContent',

  mixins: [mixin, trackerMixin],

  components: {
    EntityCard,
    EmptyState,
    ExploreSkeletons,
    FindingDrawer,
    ExploreContentTable,
  },

  props: {
    searchValue: String,
    isTableView: {
      type: Boolean,
      default: false,
    },
    smallView: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    itemsPerPage: {
      type: Number,
      default: 20,
    },
    sort: {
      type: String,
      default: '',
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    hasFiltersSelected: {
      type: Boolean,
      default: false,
    },
    selectedHeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isFindingDrawerOpen: false,
      loading: false,
      options: {
        page: this.currentPage,
        itemsPerPage: this.itemsPerPage,
      },
      results: [],
      selectedFindingId: null,
      totalItems: 0,
    }
  },

  watch: {
    loading(currentValue) {
      this.$emit('state', currentValue)
    },
    searchValue() {
      this.fetchContent()
    },
    currentPage() {
      this.options.page = this.currentPage
      this.fetchContent()
    },
    sort() {
      this.fetchContent()
    },
    filters() {
      this.fetchContent()
    },
    itemsPerPage() {
      this.options.itemsPerPage = this.itemsPerPage
      this.fetchContent()
    },
  },

  mounted() {
    this.fetchContent()
  },

  computed: {
    ...mapGetters(['organizationId']),

    hasResults() {
      return Boolean(this.results.length > 0)
    },

    sortOptions() {
      const [by, order] = this.sort.split('-')
      return by && order ? { by, order } : {}
    },

    currentLanguage() {
      return this.$i18n.i18next.language
    },

    subtitleEmptyState() {
      return this.hasFiltersSelected
        ? this.$t('explore.empty_state_filters_subtitle')
        : this.$t('explore.empty_state_subtitle')
    },
  },

  methods: {
    fetchContent: _.debounce(async function () {
      try {
        this.loading = true
        const response = await api.explore.getContent({
          terms: this.searchValue,
          organizationId: this.organizationId,
          includeMatchingFields: true,
          limit: this.options.itemsPerPage,
          from: (this.options.page - 1) * this.itemsPerPage,
          sort: this.sortOptions,
          filters: {
            ..._.omit(this.filters, 'organization'), // We have detected a case in which the organization filter was passed as filter causing a HTTP 500 error. The root case is still under investigation.
            isPrivate: false,
          },
          projections: [
            'assignees',
            'coach',
            'createdAt',
            'createdBy',
            'endAt',
            'predictedFinancialImpactInUSD',
            'hasReplicas',
            'image',
            'isReplica',
            'linkedEntityId',
            'linkedEntityType',
            'name',
            'organization',
            'owner',
            'priority',
            'siteAttributes',
            'startAt',
            'status',
            'tags',
            'totalLikes',
            'totalRevs',
            'updatedAt',
            'validations',
            'findingId',
            'routineId',
            'routine',
          ],
          expansions: [
            'assignees',
            'coach',
            'createdBy',
            'image',
            'owner',
            'priority',
            'routine',
            'siteAttributes',
            'tags',
            'validations',
          ],
        })

        this.results = response.data.results

        this.totalItems = response.data.totalRecordsInData

        if (this.totalItems === 0)
          this.trackExploreSearch({
            action: 'emptySearch',
          })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
        this.updateParent()
      }
    }, 500),

    getEntityTitle(item) {
      return item.situation ?? item.name
    },

    handleChangePage(event) {
      this.options.page = event
      this.trackExplorePaginate({
        page: event,
        view: VIEW_OPTIONS.table,
      })
      this.updateParent()
    },

    handleChangeItemsPerPage(event) {
      this.options.itemsPerPage = event
      this.fetchContent()
    },

    updateParent() {
      this.$emit('change', {
        totalItems: this.totalItems,
        currentPage: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
      })
    },

    handleOpenFindingDrawer(findingId) {
      this.selectedFindingId = findingId
      this.isFindingDrawerOpen = true
    },

    handleFindingDrawerClose() {
      this.isFindingDrawerOpen = false
      this.selectedFindingId = null
    },

    handleOpenSummarizeDialog(data) {
      eventHub.$emit(HUB_EVENTS.AI.HUB_DIALOG_OPEN, {
        rev: data,
        createSummary: true,
      })
    },
  },
}
</script>

<style>
.sticky-empty-state {
  position: sticky;
  top: 120px;
}
</style>
